import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, Loader2 } from "lucide-react";

/**
 * 21 CFR Part 11 / EU Annex 11 compliant e-signature dialog.
 * On success, calls onSigned(signature) with the created signature.
 */
export default function ESignatureDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  action,
  defaultMeaning = "Approval",
  title = "Electronic Signature Required",
  description,
  onSigned,
}) {
  const { user } = useAuth();
  const [meaning, setMeaning] = useState(defaultMeaning);
  const [reason, setReason] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const isGoogle = user?.auth_provider === "google";

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      toast.error("Reason for change is required (21 CFR Part 11)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/esign", {
        entity_type: entityType,
        entity_id: entityId,
        action,
        meaning,
        reason,
        password,
      });
      toast.success("Signature recorded");
      onSigned?.(data);
      onOpenChange(false);
      setPassword("");
      setReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signature failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="esign-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
              21 CFR Part 11 • EU Annex 11
            </span>
          </div>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>
            {description || `You are about to perform "${action}". Confirm your identity and provide a reason for this change.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="p-3 bg-slate-50 rounded border border-slate-200 text-xs font-mono space-y-1">
            <div><span className="text-slate-500">Signer:</span> {user?.name} &lt;{user?.email}&gt;</div>
            <div><span className="text-slate-500">Roles:</span> {(user?.roles || []).join(", ") || "—"}</div>
            <div><span className="text-slate-500">Timestamp:</span> {new Date().toISOString()}</div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Signature meaning</Label>
            <Select value={meaning} onValueChange={setMeaning}>
              <SelectTrigger className="mt-1.5" data-testid="esign-meaning">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Author", "Review", "Approval", "Verification", "Effectiveness Check", "Training Completion", "Witness"].map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">Reason for change / signing *</Label>
            <Textarea
              data-testid="esign-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1.5"
              placeholder="e.g. 'CAPA effectiveness verified via 3-month post-implementation review'"
              required
            />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-700">
              {isGoogle ? "Type 'google-sso-confirm' to attest identity *" : "Password *"}
            </Label>
            <Input
              data-testid="esign-password"
              type={isGoogle ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 font-mono"
              placeholder={isGoogle ? "google-sso-confirm" : "Your password"}
              required
              autoComplete="new-password"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="border-slate-300 font-semibold">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="esign-submit"
                    className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing…</> : "Sign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
